import React, { useEffect, useState, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BRAND_NAME } from '../../config/branding';
import useAuth from '../../hooks/useAuth';
import { RootState, useAppDispatch } from '../../store';
import {
  openTermsModal,
  openPrivacyModal,
  openDisclaimerModal,
} from '../../store/slices/modalsSlice';
import LoginModal from '../Auth/LoginModal';
import RegisterModal from '../Auth/RegisterModal';
import ForgotPasswordModal from '../Auth/ForgotPasswordModal';
import PricingPage from '../PricingPage';
import HeroSection from './HeroSection';
import MarketIndicesRow from './MarketIndicesRow';
import FeatureGrid from './FeatureGrid';
import WorkflowSection from './WorkflowSection';
import AgentsSection from './AgentsSection';
import StatsGrid from './StatsGrid';
import WhyItMatters from './WhyItMatters';

// Lazy-load legal modals (same components used in App.tsx for authenticated users)
const TermsModal = React.lazy(() => import('../TermsModal'));
const PrivacyModal = React.lazy(() => import('../PrivacyModal'));
const DisclaimerModal = React.lazy(() => import('../DisclaimerModal'));

const NewLandingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const modals = useSelector((state: RootState) => state.modals);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingRedirectTab, setPendingRedirectTab] = useState<string | null>(
    null
  );
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerInitialError, setRegisterInitialError] = useState<string | null>(null);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState<{
    email: string;
    verificationSent: boolean;
  } | null>(null);
  // Single-banner pricing notification (PR-10): merges authHint + checkout success/cancel
  // into one dismissible banner so the pricing section never stacks three alerts.
  type CheckoutKind = 'success' | 'warning' | 'info';
  const [checkoutBanner, setCheckoutBanner] = useState<{
    kind: CheckoutKind;
    text: string;
  } | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Single banner-resolution effect - picks success/warning from URL params,
  // else falls back to the auth-hint info banner for unauthenticated users.
  // Reads neither setCheckoutBanner's own state nor itself in deps; safe.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const isSuccess =
      params.get('checkout') === 'success' ||
      params.get('success') === 'true' ||
      params.get('session_id') !== null;
    const isCancelled =
      params.get('checkout') === 'cancel' ||
      params.get('canceled') === 'true' ||
      params.get('cancel') === 'true';

    let next: { kind: CheckoutKind; text: string } | null = null;
    if (isSuccess) {
      next = {
        kind: 'success',
        text: t('landing.pricing.checkoutSuccess'),
      };
    } else if (isCancelled) {
      next = {
        kind: 'warning',
        text: t('landing.pricing.checkoutCancel'),
      };
    } else if (!isAuthenticated) {
      next = {
        kind: 'info',
        text: t('landing.pricing.authHint'),
      };
    }

    setCheckoutBanner((prev) => {
      if (prev?.kind === next?.kind && prev?.text === next?.text) {
        return prev;
      }
      return next;
    });

    if (isSuccess || isCancelled) {
      const next = `${window.location.pathname}${window.location.hash}`;
      window.history.replaceState({}, document.title, next);
    }
  }, [isAuthenticated, t]);

  // Listen for login modal events from child components.
  // The new AgentsSection (Phase 3) attaches `detail.redirectTo='agents'`
  // so the post-login navigation can deep-link into the Agent Console
  // tab. Older callers (HeroSection, header CTA) dispatch without detail
  // and continue to land on the default tab.
  useEffect(() => {
    const handleOpenLoginModal = (e: Event) => {
      const ce = e as CustomEvent<{ redirectTo?: string }>;
      const target = ce?.detail?.redirectTo;
      if (typeof target === 'string' && target.length > 0) {
        setPendingRedirectTab(target);
      } else {
        setPendingRedirectTab(null);
      }
      // Close the sibling modal so login/register are mutually exclusive -
      // otherwise a trial CTA could render Register while a previously-opened
      // Login modal stays mounted, surfacing Sign-In instead of Sign-Up.
      setShowRegisterModal(false);
      setShowLoginModal(true);
    };
    const handleOpenRegisterModal = () => {
      setPendingRedirectTab(null);
      setShowLoginModal(false);
      setShowRegisterModal(true);
    };
    window.addEventListener('openLoginModal', handleOpenLoginModal as EventListener);
    window.addEventListener(
      'openRegisterModal',
      handleOpenRegisterModal as EventListener
    );
    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModal as EventListener);
      window.removeEventListener(
        'openRegisterModal',
        handleOpenRegisterModal as EventListener
      );
    };
  }, []);

  useEffect(() => {
    if (typeof sessionStorage !== 'undefined') {
      const stashed = sessionStorage.getItem('pending_register_error');
      if (stashed) {
        setRegisterInitialError(stashed);
        setShowRegisterModal(true);
        sessionStorage.removeItem('pending_register_error');
      }
    }
  }, []);

  // Scroll reveal animation
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const scrollRevealElements = document.querySelectorAll('.scroll-reveal');
    scrollRevealElements.forEach((el) => observer.observe(el));

    return () => {
      scrollRevealElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const handleSwitchToRegister = (initialError?: string) => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
    if (initialError) setRegisterInitialError(initialError);
  };

  const handleSwitchToLogin = (info?: {
    email: string;
    verificationSent: boolean;
  }) => {
    setShowRegisterModal(false);
    if (info) {
      setVerificationInfo(info);
      setShowLoginModal(true);
    } else {
      setVerificationInfo(null);
      setShowLoginModal(true);
    }
  };

  const handleSwitchToForgotPassword = () => {
    setShowLoginModal(false);
    setShowForgotPasswordModal(true);
  };

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const bannerStyle = (kind: CheckoutKind): React.CSSProperties => {
    if (kind === 'success') {
      return {
        backgroundColor: 'rgba(34, 197, 94, 0.12)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        color: '#bbf7d0',
      };
    }
    if (kind === 'warning') {
      return {
        backgroundColor: 'rgba(245, 158, 11, 0.12)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        color: '#fde68a',
      };
    }
    return {
      backgroundColor: 'rgba(96, 165, 250, 0.12)',
      border: '1px solid rgba(96, 165, 250, 0.3)',
      color: '#bfdbfe',
    };
  };

  return (
    <div
      className="landing-root text-white"
      data-testid="landing-root"
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--surface)',
      }}
    >
      {/* Subtle gradient overlays for depth */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 100% 80% at 50% -10%, rgba(201, 162, 39, 0.02) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 100% 0%, rgba(166, 124, 82, 0.02) 0%, transparent 50%)',
          zIndex: 0,
        }}
      />
      {/* Header */}
      <header
        className="sticky-top"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'rgba(14, 19, 30, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        <div className="container py-2 d-flex justify-content-between align-items-center gap-2">
          <div
            className="d-flex align-items-center gap-2"
            style={{ minWidth: 0 }}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo-app.png`}
              alt={BRAND_NAME}
              className="flex-shrink-0"
              style={{ width: 36, height: 36, objectFit: 'contain' }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                className="fw-semibold text-truncate"
                style={{
                  fontFamily: 'var(--font-display)',
                  color: 'var(--text-primary)',
                  fontSize: '0.95rem',
                  letterSpacing: '-0.01em',
                }}
              >
                {BRAND_NAME}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="btn btn-link text-decoration-none px-2 d-none d-sm-inline-block"
              style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}
              onClick={() => scrollToElement('pricing')}
            >
              {t('landing.cta.headerPricing')}
            </button>
            {isAuthenticated ? (
              <button
                type="button"
                className="cta-button px-3 py-1 px-md-4 py-md-2"
                style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                onClick={() => {
                  window.location.href = '/app';
                }}
                data-testid="header-cta"
              >
                <span>{t('landing.cta.headerAuthed')}</span>
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-link text-decoration-none px-2"
                style={{ color: 'var(--text-primary)', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                onClick={() => setShowLoginModal(true)}
                data-testid="header-login"
              >
                {t('landing.cta.login', 'Log in')}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main style={{ position: 'relative', zIndex: 1 }}>
        {/*
          Each top-level landing section is wrapped in `landing-section`
          so anchor scrolling (e.g. ?tab= deep-links via the sticky
          header) leaves a 72px gap. The class is applied via the
          wrapping div because the inner section components own their
          own root <section> element and we don't want every component
          to know about page-level layout chrome. AgentsSection bakes
          the class onto its own <section>, so no wrapper there.
        */}
        <div className="landing-section">
          {/* US indices - live Yahoo Finance widgets */}
          <MarketIndicesRow />
        </div>

        <div className="landing-section">
          {/* Hero Section */}
          <HeroSection />
        </div>

        <div className="landing-section">
          {/* Workflow - single section, replaces both the legacy hero glass-card
              and the old "How It Works" four-step block. */}
          <WorkflowSection />
        </div>

        {/* Agents - Phase 3: pitch the autonomous-agents loop and link
            into the Agent Console (post-login deep-link via ?tab=agents). */}
        <AgentsSection />

        <div className="landing-section">
          {/* Features Section */}
          <FeatureGrid />
        </div>

        <div className="landing-section">
          {/* By the numbers (factual stats) - replaces "At a glance" */}
          <StatsGrid />
        </div>

        <div className="landing-section">
          {/* Why it matters - short paragraph that bridges proof to pricing */}
          <WhyItMatters />
        </div>

        {/* Pricing Section */}
        <section id="pricing" className="landing-section py-4 py-md-5">
          <div className="container">
            <div
              className="p-3 p-md-4 p-lg-5 glass-card"
              style={{
                borderRadius: 'var(--radius-xl, 12px)',
              }}
            >
              {checkoutBanner && !bannerDismissed && (
                <div
                  className="alert mb-4 d-flex align-items-start gap-2"
                  style={bannerStyle(checkoutBanner.kind)}
                  role={checkoutBanner.kind === 'warning' ? 'alert' : 'status'}
                  aria-live={
                    checkoutBanner.kind === 'warning' ? 'assertive' : 'polite'
                  }
                  data-testid="pricing-banner"
                >
                  <span className="flex-grow-1">{checkoutBanner.text}</span>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    aria-label="Dismiss"
                    onClick={() => setBannerDismissed(true)}
                    style={{ filter: 'invert(0.6)' }}
                  />
                </div>
              )}

              <div className="row g-4 g-lg-5 align-items-center">
                {/* Left: the pitch. Fills the width so the plan card on the
                    right no longer floats alone. */}
                <div className="col-12 col-lg-7 text-center text-lg-start">
                  <p
                    className="landing-eyebrow mb-2"
                    style={{ color: 'var(--accent-primary)' }}
                  >
                    {t('landing.pricing.eyebrow')}
                  </p>
                  <h2
                    className="landing-pricing__headline mb-3"
                    style={{
                      fontFamily: 'var(--font-display)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t('landing.pricing.headline')}
                  </h2>
                  <p
                    className="landing-section__sub mb-0 mx-auto mx-lg-0"
                    style={{
                      color: 'var(--text-secondary)',
                      maxWidth: 560,
                      lineHeight: 1.7,
                    }}
                  >
                    {t('landing.pricing.sub')}
                  </p>
                </div>

                {/* Right: the plan card in marketing mode - pure plan info
                    (price + features) with ONE working CTA that registers for
                    the free trial. No dead "Subscribe" button or billing-period
                    toggle on the public page. */}
                <div className="col-12 col-lg-5">
                  <PricingPage
                    embedded
                    marketing
                    marketingCtaLabel={t('landing.cta.primary')}
                    onMarketingCta={() => setShowRegisterModal(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section
          className="py-4 py-md-5 text-center landing-final-cta landing-section"
          aria-labelledby="final-cta-headline"
        >
          <div className="container">
            <div
              className="glass-card p-4 p-md-5"
              style={{
                maxWidth: '820px',
                margin: '0 auto',
                background:
                  'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(242, 202, 80, 0.08), transparent 60%)',
              }}
            >
              <h3
                id="final-cta-headline"
                className="mb-2"
                style={{
                  fontFamily: 'var(--font-display)',
                  color: 'var(--text-primary)',
                }}
              >
                {t('landing.finalCta.headline')}
              </h3>
              <p
                className="landing-section__sub mb-3 mx-auto"
                style={{ color: 'var(--text-secondary)', maxWidth: 600 }}
              >
                {t('landing.finalCta.sub')}
              </p>
              <div className="d-flex flex-wrap gap-3 justify-content-center">
                <button
                  type="button"
                  className="cta-button px-5 py-3"
                  onClick={() => {
                    if (isAuthenticated) {
                      window.location.href = '/app';
                    } else {
                      setShowRegisterModal(true);
                    }
                  }}
                  data-testid="final-cta-primary"
                >
                  <span className="d-flex align-items-center gap-2">
                    {t('landing.finalCta.primary')}
                    <CheckCircle2 size={18} />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Disclaimer - moved BELOW pricing per Part I1/I5 so visitors who
            scroll past pricing don't bounce into legal. */}
        <section
          id="legal"
          className="py-4 py-md-5 landing-section"
          style={{ backgroundColor: 'var(--surface_container_low)' }}
        >
          <div className="container" style={{ maxWidth: 820 }}>
            <div
              className="d-flex flex-column gap-2 p-4 rounded-3"
              style={{
                border: '1px solid var(--border-ghost)',
                backgroundColor: 'var(--surface_container)',
              }}
            >
              <h2
                className="landing-card__title fw-semibold mb-1"
                style={{
                  fontFamily: 'var(--font-display)',
                  color: 'var(--text-primary)',
                  fontSize: '1.0625rem',
                }}
              >
                {t('landing.disclaimer.title')}
              </h2>
              <p
                className="landing-disclaimer__body mb-0"
                style={{ color: 'var(--text-secondary)' }}
              >
                {t('landing.disclaimer.body')}
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer
          className="py-3 text-center border-top"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'var(--surface_container_low)',
          }}
        >
          <div className="container">
            <div
              className="mb-2"
              style={{ color: 'var(--text-muted)' }}
            >
              {t('landing.footer.tagline')}
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              <span>
                &copy; {new Date().getFullYear()} {BRAND_NAME}
              </span>
              <span className="mx-2">|</span>
              <button
                type="button"
                onClick={() => dispatch(openTermsModal())}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  fontSize: 'inherit',
                }}
              >
                {t('landing.footer.terms')}
              </button>
              <span className="mx-2">|</span>
              <button
                type="button"
                onClick={() => dispatch(openPrivacyModal())}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  fontSize: 'inherit',
                }}
              >
                {t('landing.footer.privacy')}
              </button>
              <span className="mx-2">|</span>
              <button
                type="button"
                onClick={() => dispatch(openDisclaimerModal())}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  fontSize: 'inherit',
                }}
              >
                {t('landing.footer.disclaimerLink')}
              </button>
              <span className="mx-2">|</span>
              <a
                href="mailto:support@stockbai.com"
                className="text-decoration-none"
                style={{ color: 'var(--text-muted)' }}
              >
                {t('landing.footer.contact')}
              </a>
            </div>
          </div>
        </footer>
      </main>

      {/* Login/Register Modals */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setVerificationInfo(null);
          setPendingRedirectTab(null);
        }}
        onSwitchToRegister={handleSwitchToRegister}
        onSwitchToForgotPassword={handleSwitchToForgotPassword}
        redirectTo={pendingRedirectTab}
        {...(verificationInfo
          ? {
              verificationEmail: verificationInfo.email,
              showVerificationState: true,
              verificationSent: verificationInfo.verificationSent,
            }
          : {})}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => {
          setShowRegisterModal(false);
          setRegisterInitialError(null);
        }}
        onSwitchToLogin={handleSwitchToLogin}
        initialError={registerInitialError}
      />
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
        onSwitchToLogin={() => {
          setShowForgotPasswordModal(false);
          setShowLoginModal(true);
        }}
      />

      {/* Legal modals - must render here for unauthenticated users
          (App.tsx modals are behind AuthGate). */}
      <Suspense fallback={null}>
        {modals.termsModal && <TermsModal />}
        {modals.privacyModal && <PrivacyModal />}
        {modals.disclaimerModal && <DisclaimerModal />}
      </Suspense>
    </div>
  );
};

export default NewLandingPage;
